import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, RotateCcw, Minus, Plus, ScanLine, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useCountingInvoice,
  useIncrementCountingVariation,
  useSetCountingVariationCount,
  useResetCountingItem,
  useResetAllCounting,
  useSetCountingStatus,
  useAddCountingVariation,
} from "@/hooks/use-counting";
import { playSuccess, playError } from "@/lib/sounds";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { isolateScannerKeys, useScannerFocusGuard } from "@/hooks/use-scanner-input";

function parseColor(sku: string, vendorCode: string): string | null {
  const suffix = sku.startsWith(vendorCode + "/") ? sku.slice(vendorCode.length + 1) : null;
  if (!suffix) return null;
  const parts = suffix.split("/");
  return parts.length >= 2 ? parts[0]! : null;
}

export function CountingCounterPage() {
  const { invoiceId: rawInvoiceId } = useParams({ strict: false });
  const invoiceId = rawInvoiceId ?? "";
  const { data: invoice, isLoading } = useCountingInvoice(invoiceId || null);
  const increment = useIncrementCountingVariation();
  const setCount = useSetCountingVariationCount();
  const resetItem = useResetCountingItem();
  const resetAll = useResetAllCounting();
  const setStatus = useSetCountingStatus();
  const addVariation = useAddCountingVariation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [localCounts, setLocalCounts] = useState<Record<string, number>>({});
  const [scanInput, setScanInput] = useState("");
  const [lastScan, setLastScan] = useState<{ sku: string; ok: boolean } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scanningRef = useRef(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useScannerFocusGuard(inputRef);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (invoice && invoice.status === "OPEN") {
      setStatus.mutate({ id: invoiceId, status: "COUNTING", expectedStatus: "OPEN" });
    }
  }, [invoice?.status]);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ["counting", invoiceId] });
    }, 1500);
  }, [invoiceId, queryClient]);

  function effectiveCount(varId: string, serverValue: number): number {
    return localCounts[varId] !== undefined ? localCounts[varId] : serverValue;
  }

  async function handleScan(e: React.KeyboardEvent) {
    if (e.key !== "Enter" || !scanInput.trim() || scanningRef.current) return;
    e.preventDefault();
    scanningRef.current = true;

    const raw = scanInput.trim();
    setScanInput("");

    if (!invoice) { scanningRef.current = false; return; }

    // Normalize: lowercase + treat -, _, whitespace, backslash as /
    const normalize = (s: string) => s.toLowerCase().replace(/[\s_\\-]+/g, "/").replace(/\/+/g, "/").replace(/^\/|\/$/g, "");
    const target = normalize(raw);

    let foundVar: any = null;
    for (const item of invoice.items) {
      // exact normalized match
      let v = item.variations.find((v: any) => normalize(v.sku) === target);
      // fallback: scan contains only suffix (no vendor code) → match by suffix
      if (!v) {
        v = item.variations.find((v: any) => {
          const skuNorm = normalize(v.sku);
          const codeNorm = normalize(item.vendorCode);
          const suffix = skuNorm.startsWith(codeNorm + "/") ? skuNorm.slice(codeNorm.length + 1) : skuNorm;
          return suffix === target;
        });
      }
      if (v) { foundVar = v; break; }
    }

    // Fallback: artikal bez varijacija — match na vendorCode, lazy-create flat varijaciju
    if (!foundVar) {
      const flatItem = invoice.items.find((it: any) =>
        it.variations.length === 0 && normalize(it.vendorCode) === target
      );
      if (flatItem) {
        try {
          foundVar = await addVariation.mutateAsync({
            itemId: flatItem.id,
            invoiceId,
            sku: flatItem.vendorCode,
            expected_qty: flatItem.quantity || 0,
          });
        } catch (e: any) {
          playError();
          toast.error(e?.message || "Greška pri kreiranju varijacije");
          scanningRef.current = false;
          return;
        }
      }
    }

    const sku = foundVar ? foundVar.sku : raw;

    if (!foundVar) {
      playError();
      setLastScan({ sku, ok: false });
      toast.error("SKU nije na fakturi");
      scanningRef.current = false;
      return;
    }

    const current = effectiveCount(foundVar.id, foundVar.countedQty);
    if (current >= foundVar.expectedQty) {
      playError();
      setLastScan({ sku, ok: false });
      toast.error("Već prebrojano");
      scanningRef.current = false;
      return;
    }

    setLocalCounts((prev) => ({ ...prev, [foundVar.id]: current + 1 }));
    setLastScan({ sku, ok: true });
    playSuccess();

    try {
      const result = await increment.mutateAsync({ varId: foundVar.id, delta: 1, invoiceId, requestId: crypto.randomUUID() });
      setLocalCounts((prev) => ({ ...prev, [foundVar.id]: result.countedQty }));
      scheduleRefresh();
    } catch (e: any) {
      playError();
      toast.error(e?.message || "Greška pri skeniranju");
      setLocalCounts((prev) => {
        const copy = { ...prev };
        delete copy[foundVar.id];
        return copy;
      });
    }

    scanningRef.current = false;
  }

  async function ensureFlatVariation(item: any): Promise<any> {
    if (item.variations.length > 0) return item.variations[0];
    return await addVariation.mutateAsync({
      itemId: item.id,
      invoiceId,
      sku: item.vendorCode,
      expected_qty: item.quantity || 0,
    });
  }

  async function handleFlatIncrement(item: any, delta: number) {
    try {
      const v = await ensureFlatVariation(item);
      const result = await increment.mutateAsync({ varId: v.id, delta, invoiceId, requestId: crypto.randomUUID() });
      setLocalCounts((prev) => ({ ...prev, [v.id]: result.countedQty }));
      if (delta > 0) playSuccess();
      scheduleRefresh();
    } catch (e: any) {
      playError();
      toast.error(e?.message || "Greška");
    }
  }

  async function handleManualIncrement(varId: string, delta: number) {
    try {
      const result = await increment.mutateAsync({ varId, delta, invoiceId, requestId: crypto.randomUUID() });
      setLocalCounts((prev) => ({ ...prev, [varId]: result.countedQty }));
      if (delta > 0) playSuccess();
      scheduleRefresh();
    } catch (e: any) {
      playError();
      toast.error(e?.message || "Greška pri ažuriranju brojanja");
    }
  }

  async function handleSetCount(varId: string, value: number) {
    try {
      const result = await setCount.mutateAsync({ varId, value, invoiceId });
      setLocalCounts((prev) => ({ ...prev, [varId]: result.countedQty }));
      scheduleRefresh();
    } catch (e: any) {
      toast.error(e?.message || "Greška pri postavljanju vrijednosti");
    }
  }

  async function handleResetItem(itemId: string) {
    if (!confirm("Resetirati brojanje za ovaj artikal?")) return;
    await resetItem.mutateAsync({ itemId, invoiceId });
    setLocalCounts({});
    toast.success("Resetirano");
  }

  async function handleResetAll() {
    if (!confirm("Resetirati SVE brojanje?")) return;
    await resetAll.mutateAsync({ invoiceId });
    setLocalCounts({});
    toast.success("Sve resetirano");
  }

  async function handleFinish() {
    if (!confirm("Završiti brojanje? Nakon toga se faktura zaključava.")) return;
    await setStatus.mutateAsync({ id: invoiceId, status: "DONE" });
    toast.success("Brojanje završeno");
    navigate({ to: "/warehouse/brojanje-robe/$invoiceId", params: { invoiceId } });
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground/60 animate-spin" />
      </div>
    );
  }
  if (!invoice) return <div className="p-4 text-sm text-destructive">Nije pronađeno.</div>;

  if (invoice.status === "DONE") {
    return (
      <div className="p-4 flex flex-col gap-3">
        <p className="text-sm">Brojanje je završeno. Za izmjene prvo ponovo otvorite fakturu.</p>
        <Link to="/warehouse/brojanje-robe/$invoiceId" params={{ invoiceId }}>
          <Button variant="outline">Nazad na fakturu</Button>
        </Link>
      </div>
    );
  }

  const totalExpected = invoice.items.reduce((s: number, i: any) => s + i.variations.reduce((vs: number, v: any) => vs + v.expectedQty, 0), 0);
  const totalCounted = invoice.items.reduce((s: number, i: any) => s + i.variations.reduce((vs: number, v: any) => vs + effectiveCount(v.id, v.countedQty), 0), 0);
  const progressPct = totalExpected > 0 ? Math.round((totalCounted / totalExpected) * 100) : 0;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Scanning header — instrument panel feel */}
      <div className="sticky top-0 z-10 border-b border-foreground/10 bg-background">
        {/* Progress bar */}
        <div className="h-0.5 bg-foreground/[0.04]">
          <div
            className="h-full bg-foreground transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="px-3 pt-2 pb-2.5">
          {/* Top row */}
          <div className="flex items-center gap-2 mb-2">
            <Link to="/warehouse/brojanje-robe/$invoiceId" params={{ invoiceId }}>
              <Button variant="ghost" size="icon-xs"><ArrowLeft className="h-4 w-4" /></Button>
            </Link>
            <span className="text-xs font-medium truncate flex-1 text-muted-foreground">{invoice.name}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-semibold tabular-nums tracking-tighter">{totalCounted}</span>
              <span className="text-xs text-muted-foreground">/ {totalExpected}</span>
            </div>
            <Button variant="ghost" size="icon-xs" onClick={handleResetAll} title="Resetiraj sve">
              <RotateCcw className="h-3.5 w-3.5" />
            </Button>
            {invoice.status !== "DONE" && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleFinish}
                disabled={setStatus.isPending}
                title="Završi brojanje"
                className="text-green-600 dark:text-green-400"
              >
                <CheckCircle className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Scanner input */}
          <div className="relative">
            <ScanLine className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
            <input
              ref={inputRef}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyDown={isolateScannerKeys(handleScan)}
              placeholder="Skeniraj SKU..."
              className="w-full h-10 pl-9 pr-3 text-sm font-mono bg-foreground/[0.03] border border-foreground/10 focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/10 placeholder:text-muted-foreground/40 transition-colors"
              autoComplete="off"
              autoFocus
            />
            {lastScan && (
              <div className={`absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-medium tabular-nums ${lastScan.ok ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
                {lastScan.ok ? "OK" : "ERR"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto" onClick={() => inputRef.current?.focus()}>
        <div className="p-3 flex flex-col gap-3">
          {invoice.items.map((item: any) => {
            const itemCounted = item.variations.reduce((s: number, v: any) => s + effectiveCount(v.id, v.countedQty), 0);
            const itemExpected = item.variations.reduce((s: number, v: any) => s + v.expectedQty, 0);
            const itemDone = itemExpected > 0 && itemCounted >= itemExpected;

            const grouped = new Map<string, any[]>();
            for (const v of item.variations) {
              const color = parseColor(v.sku, item.vendorCode) || "__flat__";
              if (!grouped.has(color)) grouped.set(color, []);
              grouped.get(color)!.push(v);
            }

            return (
              <div key={item.id} className={`ring-1 transition-colors ${itemDone ? "ring-green-500/20 bg-green-500/[0.02]" : "ring-foreground/[0.06]"}`}>
                {/* Item header */}
                <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-foreground/[0.04]">
                  <span className="text-[11px] font-semibold">{item.vendorCode}</span>
                  <span className="text-[11px] text-muted-foreground truncate flex-1">{item.name}</span>
                  <span className="text-[11px] tabular-nums font-medium">
                    {itemCounted}<span className="text-muted-foreground/50">/{itemExpected}</span>
                  </span>
                  <Button variant="ghost" size="icon-xs" onClick={() => handleResetItem(item.id)} title="Resetiraj">
                    <RotateCcw className="h-2.5 w-2.5" />
                  </Button>
                </div>

                {/* Variations */}
                <div className="px-1.5 py-1.5">
                  {item.variations.length === 0 && (
                    <div className="flex items-center gap-1 px-2 py-1.5 text-[11px] bg-foreground/[0.015] hover:bg-foreground/[0.03] transition-colors">
                      <span className="font-medium tabular-nums flex-1 truncate text-muted-foreground">{item.vendorCode}</span>
                      <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon-xs" onClick={() => handleFlatIncrement(item, -1)} className="h-5 w-5">
                          <Minus className="h-2.5 w-2.5" />
                        </Button>
                        <span className="w-6 text-center text-[11px] font-semibold tabular-nums">0</span>
                        <span className="text-muted-foreground/40 text-[10px] tabular-nums">/{item.quantity}</span>
                        <Button variant="ghost" size="icon-xs" onClick={() => handleFlatIncrement(item, 1)} className="h-5 w-5">
                          <Plus className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {Array.from(grouped.entries()).map(([color, vars]) => (
                    <div key={color} className="mb-1 last:mb-0">
                      {color !== "__flat__" && (
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-medium px-1 mb-0.5">
                          {color}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px">
                        {vars.map((v: any) => {
                          const counted = effectiveCount(v.id, v.countedQty);
                          const done = counted >= v.expectedQty;
                          const pct = v.expectedQty > 0 ? (counted / v.expectedQty) * 100 : 0;
                          return (
                            <div
                              key={v.id}
                              className={`relative flex items-center gap-1 px-2 py-1.5 text-[11px] overflow-hidden ${done ? "bg-green-500/[0.06]" : "bg-foreground/[0.015] hover:bg-foreground/[0.03]"} transition-colors`}
                            >
                              {/* Mini progress bar behind content */}
                              {!done && pct > 0 && (
                                <div
                                  className="absolute inset-y-0 left-0 bg-foreground/[0.04] transition-all duration-200"
                                  style={{ width: `${pct}%` }}
                                />
                              )}
                              <span className="relative font-medium tabular-nums flex-1 truncate">{v.sku.split("/").pop()}</span>
                              <div className="relative flex items-center gap-0.5">
                                <Button variant="ghost" size="icon-xs" onClick={() => handleManualIncrement(v.id, -1)} disabled={counted <= 0} className="h-5 w-5">
                                  <Minus className="h-2.5 w-2.5" />
                                </Button>
                                <input
                                  type="number"
                                  className="w-6 text-center text-[11px] font-semibold bg-transparent tabular-nums focus:outline-none"
                                  value={counted}
                                  onChange={(e) => handleSetCount(v.id, Number(e.target.value))}
                                  min={0}
                                  max={v.expectedQty}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-muted-foreground/40 text-[10px] tabular-nums">/{v.expectedQty}</span>
                                <Button variant="ghost" size="icon-xs" onClick={() => handleManualIncrement(v.id, 1)} disabled={done} className="h-5 w-5">
                                  <Plus className="h-2.5 w-2.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
